import { api, type ApiClient } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'
import {
  envelopeAuditListDtoSchema,
  envelopeDtoSchema,
  envelopeListDtoSchema,
  type EnvelopeAuditListDto,
  type EnvelopeDto,
  type EnvelopeListDto,
} from '@lib/dto/envelope.dto'
import { questionThreadListDtoSchema, type QuestionThreadListDto } from '@lib/dto/question.dto'
import type { ExtendEnvelopeInput, OpsEnvelopeSearchQuery, RemindEnvelopeInput, VoidEnvelopeInput } from '@lib/input/envelope.input'
import type { IEnvelopeService } from '@/types/EnvelopeService.types'

/** The search as URL parameters, leaving out anything unset. */
export function envelopeQueryString(query: OpsEnvelopeSearchQuery | undefined): string {
  if (!query) return ''
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value))
  }
  const text = params.toString()
  return text.length > 0 ? `?${text}` : ''
}

/**
 * Envelope servicing at one API base: `/ops/envelopes` with the staff session,
 * or `/dashboard/tenants/:id/modes/:mode/envelopes` with the tenant's (or
 * staff view-as) session. Logs failures and rethrows.
 */
export class EnvelopeService implements IEnvelopeService {
  constructor(
    private readonly client: ApiClient,
    private readonly basePath: string,
  ) {}

  private path(id: string, rest = ''): string {
    return `${this.basePath}/${encodeURIComponent(id)}${rest}`
  }

  private async logged<T>(what: string, id: string | null, call: () => Promise<T>): Promise<T> {
    try {
      return await call()
    } catch (error) {
      logger.error(`Failed to ${what}`, { ...(id ? { id } : {}), error: toErrorMessage(error) })
      throw error
    }
  }

  list(query?: OpsEnvelopeSearchQuery): Promise<EnvelopeListDto> {
    return this.logged('list envelopes', null, () =>
      this.client.get(`${this.basePath}${envelopeQueryString(query)}`, envelopeListDtoSchema),
    )
  }

  get(id: string): Promise<EnvelopeDto> {
    return this.logged('load envelope', id, () => this.client.get(this.path(id), envelopeDtoSchema))
  }

  audit(id: string): Promise<EnvelopeAuditListDto> {
    return this.logged('load audit', id, () => this.client.get(this.path(id, '/audit'), envelopeAuditListDtoSchema))
  }

  remind(id: string, input: RemindEnvelopeInput): Promise<EnvelopeDto> {
    return this.logged('remind envelope', id, () =>
      this.client.post(this.path(id, '/remind'), input, envelopeDtoSchema),
    )
  }

  extend(id: string, input: ExtendEnvelopeInput): Promise<EnvelopeDto> {
    return this.logged('extend envelope', id, () => this.client.post(this.path(id, '/extend'), input, envelopeDtoSchema))
  }

  voidEnvelope(id: string, input: VoidEnvelopeInput): Promise<EnvelopeDto> {
    return this.logged('void envelope', id, () => this.client.post(this.path(id, '/void'), input, envelopeDtoSchema))
  }

  downloadDocument(id: string, documentId: string): Promise<Blob> {
    return this.client.getBlob(this.path(id, `/documents/${encodeURIComponent(documentId)}`))
  }

  downloadCertificate(id: string): Promise<Blob> {
    return this.client.getBlob(this.path(id, '/certificate'))
  }

  downloadPacket(id: string): Promise<Blob> {
    return this.client.getBlob(this.path(id, '/packet'))
  }

  downloadUnsignedPacket(id: string): Promise<Blob> {
    return this.client.getBlob(this.path(id, '/unsigned-packet'))
  }

  listQuestions(id: string): Promise<QuestionThreadListDto> {
    return this.logged('load questions', id, () =>
      this.client.get(this.path(id, '/questions'), questionThreadListDtoSchema),
    )
  }
}

export const opsEnvelopeService = new EnvelopeService(api, '/ops/envelopes')
